// ============================================================================
// RECENT CLOSURES WIDGET — Phase 8.D
// ============================================================================
// Cases closed in the last 30 days where the officer was the assigned agent.
// outcomeCategory is the snapshot taken at close time — never re-derived
// here, so historical reports stay stable even if a closure reason is later
// recategorised.

import React from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useRecentClosures } from 'hooks/api/useRecoveryDashboard';
import { openLitigationCase } from 'components/modules/litigation-module/openLitigationCase';
import type { ClosureOutcomeCategory } from 'api/types/recoveryDashboard.types';

interface Props { refreshKey?: number; }

const CLOSURE_WINDOW_DAYS = 30;
const CLOSURE_LIMIT = 10;

const chipClass = (cat: ClosureOutcomeCategory): string => {
  switch (cat) {
    case 'FAVOURABLE':   return 'dashboard-outcome-chip favourable';
    case 'UNFAVOURABLE': return 'dashboard-outcome-chip unfavourable';
    case 'NEUTRAL':      return 'dashboard-outcome-chip neutral';
    default:             return 'dashboard-outcome-chip';
  }
};

const chipLabel = (cat: ClosureOutcomeCategory): string => {
  switch (cat) {
    case 'FAVOURABLE':   return 'WIN';
    case 'UNFAVOURABLE': return 'LOSE';
    case 'NEUTRAL':      return 'NEUTRAL';
    default:             return cat;
  }
};

const formatDate = (iso: string): string => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
};

const formatAmount = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return '';
  if (n >= 10000000) return `₹ ${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000)   return `₹ ${(n / 100000).toFixed(2)} L`;
  return `₹ ${n.toLocaleString('en-IN')}`;
};

const RecentClosuresWidget: React.FC<Props> = ({ refreshKey }) => {
  const { data, loading } = useRecentClosures(CLOSURE_WINDOW_DAYS, CLOSURE_LIMIT, refreshKey);

  return (
    <div className="dashboard-widget">
      <div className="dashboard-widget-header">
        <h3 className="dashboard-widget-title">
          <IconSystem.check size={18} />
          Recent Closures
        </h3>
        <span className="dashboard-kpi-chip">{data.length}</span>
      </div>
      <div className="dashboard-widget-subtitle">
        My closures in the last {CLOSURE_WINDOW_DAYS} days
      </div>
      <div className="dashboard-widget-body">
        {loading ? (
          <div className="dashboard-widget-loading">Loading...</div>
        ) : data.length === 0 ? (
          <div className="dashboard-widget-empty">No recent closures.</div>
        ) : (
          data.map(item => (
            <div key={item.closureId} className="dashboard-list-row" onClick={() => openLitigationCase(item.caseId)} style={{ cursor: 'pointer' }}>
              <div className="dashboard-list-row-main">
                <span className="dashboard-list-row-title">
                  {item.borrowerName || '—'} <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· {item.caseCode}</span>
                </span>
                <span className="dashboard-list-row-meta">
                  {item.closureReasonName || '—'} · {formatDate(item.closedAt)}{item.recoveredAmount ? ` · ${formatAmount(item.recoveredAmount)}` : ''}
                </span>
              </div>
              <span className={chipClass(item.outcomeCategory)}>{chipLabel(item.outcomeCategory)}</span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default RecentClosuresWidget;
